import { useState } from "react";
import { Printer } from "lucide-react";
import { Button, Modal, ToggleChipButton } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import type { TranslateFn } from "@/lib/i18n/intl";
import { countedKey } from "@/lib/i18n/plural";
import type { Invoice, RestaurantSettings } from "@/lib/api/types";
import { useLocalName } from "@/shared/localName";
import { moneyFormatter, useMoney } from "@/shared/ui";
import { translatorFor } from "@/lib/i18n/intl";
import { useStoreLogo } from "@/shared/useStoreLogo";
import { usePaperTill } from "@/shared/posTheme";
import { groupPlates, type PlateGroup } from "../lib/groupPlates";
import { receiptFooterForLocale } from "../lib/receiptFooter";
import {
  PaperHead,
  PaperItems,
  PaperSumRow,
  PaperFooter,
  PaperTotal,
  paperAmount,
  orderFact,
  paperStamp,
  peopleFacts,
  type PaperFact,
  type PaperItem,
} from "@/modules/printing/paperLayout";
import { listSubtotal } from "../lib/pricing";
import {
  CHECK_SPLIT_CHOICES,
  checkMoney,
  checkPlates,
  formatCheckClock,
  splitShares,
  type CheckMoney,
} from "../lib/preBillCheck";

/**
 * THE PRE-BILL CHECK (round 5) — the dialog the waiter prints it from, and the
 * 80mm paper the guests are handed.
 *
 * A check is the bill as it stands while the table STAYS OPEN. It is not the
 * cheque (`closeInvoice` prints that), not a payment and not a tax receipt —
 * the paper says so in words, and it carries no tax registration number for
 * the same reason: a document that must not be mistaken for a tax invoice
 * should not wear one's header.
 *
 * Both halves price the bill through `checkMoney`, which is the same live
 * total the order panel's foot shows and the server stamps when it records the
 * check — so the preview, the paper and the floor tile cannot disagree.
 */

type Format = (value: number) => string;

interface SumRow {
  key: string;
  label: string;
  value: string;
  strong?: boolean;
}

/** The figures under the lines, in the order the till's own foot uses. */
function checkSumRows(
  invoice: Invoice,
  money: CheckMoney,
  t: TranslateFn,
  format: Format,
): SumRow[] {
  const rows: SumRow[] = [
    {
      key: "subtotal",
      label: t((m) => m.invoices.subtotal),
      // The LIST subtotal when a tier discount applies — the figure the
      // discount row below is taken off, the receipt's own rule.
      value: format(
        money.discountTotal > 0
          ? listSubtotal({
              subtotal: money.subtotal,
              discountTotal: money.discountTotal,
            })
          : money.subtotal,
      ),
    },
  ];
  if (money.discountTotal > 0) {
    rows.push({
      key: "discount",
      label: t((m) => m.invoices.discountTotal),
      value: `−${format(money.discountTotal)}`,
    });
  }
  // D146 — the discount at the bill, its own «خصم» line before the charges
  // computed on what it left, so subtotal − discount + service + VAT adds up.
  if (money.billDiscount > 0) {
    rows.push({
      key: "bill-discount",
      label: t((m) => m.billDiscount.lineLabel),
      value: `−${format(money.billDiscount)}`,
    });
  }
  if (money.serviceChargeTotal > 0) {
    rows.push({
      key: "service",
      label: t(
        (m) =>
          invoice.pricesIncludeTax
            ? m.invoices.serviceChargeIncludedWithRate
            : m.invoices.serviceChargeWithRate,
        { percent: String(Number(invoice.serviceChargePercent ?? 0)) },
      ),
      value: format(money.serviceChargeTotal),
    });
  }
  if (money.vatTotal > 0) {
    rows.push({
      key: "vat",
      label: t(
        (m) =>
          invoice.pricesIncludeTax
            ? m.invoices.vatIncludedWithRate
            : m.invoices.vatWithRate,
        { percent: String(Number(invoice.vatPercent ?? 0)) },
      ),
      value: format(money.vatTotal),
    });
  }
  if (money.deliveryTotal > 0) {
    rows.push({
      key: "delivery",
      label: t((m) => m.pos.offPremiseFeeLabel),
      value: format(money.deliveryTotal),
    });
  }
  if (money.paid > 0) {
    rows.push({
      key: "paid",
      label: t((m) => m.pos.partialPaidSoFar),
      value: `−${format(money.paid)}`,
    });
  }
  rows.push({
    key: "to-pay",
    label: t((m) => m.preBillCheck.toPay),
    value: format(money.toPay),
    strong: true,
  });
  return rows;
}

/** "3 guests · 83.60 each", or each share when the piastres do not divide. */
function splitLine(
  toPay: number,
  guests: number,
  t: TranslateFn,
  format: Format,
): string {
  const shares = splitShares(toPay, guests);
  const label = t(
    countedKey(guests, {
      one: (m) => m.preBillCheck.splitGuestsOne,
      two: (m) => m.preBillCheck.splitGuestsTwo,
      few: (m) => m.preBillCheck.splitGuestsFew,
      many: (m) => m.preBillCheck.splitGuests,
    }),
    { count: guests },
  );
  return shares.every((share) => share === shares[0])
    ? t((m) => m.preBillCheck.splitEach, {
        label,
        amount: format(shares[0] ?? 0),
      })
    : t((m) => m.preBillCheck.splitUneven, {
        label,
        parts: shares.map(format).join(" + "),
      });
}

/** A plate group's sub-line — the version, the swap, the extras. */
function groupDetail(
  group: PlateGroup,
  pick: (ar: string | null, en: string | null) => string | null,
): string | null {
  const parts = [
    pick(group.comboLabel, group.comboLabelEn),
    pick(group.substitutionLabel, group.substitutionLabelEn),
    pick(group.addonLabel, group.addonLabelEn),
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * The dialog the check is printed from — the design's check modal.
 *
 * It shows the check as it will print, lets the waiter pick an even split for
 * 2–5 guests (printed arithmetic, never a tender), and hands the choice back:
 * the PAGE records the check through the command envelope and prints only once
 * that has succeeded.
 */
export function PreBillCheckDialog({
  invoice,
  tableName,
  timezone,
  storeName,
  isSubmitting,
  onCancel,
  onPrint,
}: {
  invoice: Invoice;
  tableName: string | null;
  timezone: string | null | undefined;
  /** The receipt header the paper opens with, when the store set one. */
  storeName: string | null | undefined;
  isSubmitting: boolean;
  onCancel: () => void;
  onPrint: (splitGuests: number | null) => void | Promise<void>;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const format = useMoney();
  const localName = useLocalName();
  const [splitGuests, setSplitGuests] = useState<number | null>(null);
  // PAPER (light till): the redesign's check is one receipt-like sheet — the
  // title inside its head, the split chips on the look's chip.
  const paper = usePaperTill();

  const pick = (ar: string | null, en: string | null): string | null =>
    ar === null ? null : localName({ nameAr: ar, nameEn: en });
  const groups = groupPlates(checkPlates(invoice.items), format);
  const money = checkMoney(invoice);
  const rows = checkSumRows(invoice, money, t, format);
  const clock = formatCheckClock(
    new Date().toISOString(),
    timezone,
    locale === "en" ? "en" : "ar",
  );
  const title = t((m) => m.preBillCheck.dialogTitle, {
    name:
      tableName ??
      t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence }),
  });

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={title}
      closeLabel={t((m) => m.common.close)}
      data-testid="pre-bill-check-dialog"
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={onCancel}>
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            isLoading={isSubmitting}
            leadingIcon={<Printer size={16} strokeWidth={1.8} aria-hidden="true" />}
            data-testid="pre-bill-check-print"
            onClick={() => void onPrint(splitGuests)}
          >
            {t((m) => m.preBillCheck.print)}
          </Button>
        </>
      }
    >
      <div data-ui="check-sheet" className="flex flex-col">
        <div data-ui="check-head" className="flex flex-col items-center gap-1 text-center">
          {storeName ? <span data-ui="check-store">{storeName}</span> : null}
          {paper ? (
            <span data-ui="check-title" aria-hidden="true">
              {title}
            </span>
          ) : null}
          <span data-ui="check-meta" className="text-xs text-[color:var(--text-muted)]">
            {t((m) => m.pos.invoiceNumber, { sequence: invoice.dailySequence })}
            {" · "}
            <bdi dir="ltr">{clock}</bdi>
          </span>
        </div>

        <ul data-ui="check-lines" className="flex flex-col">
          {groups.map((group) => {
            const detail = groupDetail(group, pick);
            return (
              <li
                key={group.key}
                data-ui="check-line"
                className="flex items-start gap-2.5"
              >
                <span data-ui="check-line-qty" className="shrink-0 tabular-nums">
                  {group.quantity}×
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span data-ui="check-line-name">
                    {localName({
                      nameAr: group.plateNameAr,
                      nameEn: group.plateNameEn,
                    })}
                  </span>
                  {detail ? (
                    <span data-ui="check-line-detail" className="text-xs text-[color:var(--text-muted)]">
                      {detail}
                    </span>
                  ) : null}
                </span>
                <span data-ui="check-line-amount" className="whitespace-nowrap tabular-nums">
                  {format(group.total)}
                </span>
              </li>
            );
          })}
        </ul>

        <dl data-ui="check-sums" className="flex flex-col gap-1">
          {rows.map((row) => (
            <div
              key={row.key}
              data-ui="check-sum"
              data-strong={row.strong ? "" : undefined}
              data-testid={`pre-bill-check-${row.key}`}
              className="flex justify-between gap-3"
            >
              <dt>{row.label}</dt>
              <dd className="whitespace-nowrap tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>

        <div data-ui="check-split" className="flex flex-col gap-2">
          <span data-ui="check-split-label" className="text-[length:var(--font-size-sm)] text-[color:var(--text-muted)]">
            {t((m) => m.preBillCheck.splitLabel)}
          </span>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t((m) => m.preBillCheck.splitLabel)}>
            <ToggleChipButton
              size="xs"
              active={splitGuests === null}
              aria-pressed={splitGuests === null}
              data-ui={paper ? "chip-button" : undefined}
              data-active={paper && splitGuests === null ? "" : undefined}
              data-testid="pre-bill-check-split-none"
              onClick={() => setSplitGuests(null)}
            >
              {t((m) => m.preBillCheck.splitNone)}
            </ToggleChipButton>
            {CHECK_SPLIT_CHOICES.map((guests) => (
              <ToggleChipButton
                key={guests}
                size="xs"
                active={splitGuests === guests}
                aria-pressed={splitGuests === guests}
                data-ui={paper ? "chip-button" : undefined}
                data-active={paper && splitGuests === guests ? "" : undefined}
                data-testid={`pre-bill-check-split-${guests}`}
                onClick={() => setSplitGuests(guests)}
              >
                {t(
                  countedKey(guests, {
                    one: (m) => m.preBillCheck.splitGuestsOne,
                    two: (m) => m.preBillCheck.splitGuestsTwo,
                    few: (m) => m.preBillCheck.splitGuestsFew,
                    many: (m) => m.preBillCheck.splitGuests,
                  }),
                  { count: guests },
                )}
              </ToggleChipButton>
            ))}
          </div>
          {splitGuests !== null ? (
            <span data-ui="check-split-line" data-testid="pre-bill-check-split-line" className="font-semibold tabular-nums">
              {splitLine(money.toPay, splitGuests, t, format)}
            </span>
          ) : null}
        </div>

        <p data-ui="check-note" className="text-xs text-[color:var(--text-muted)]">
          {t((m) => m.preBillCheck.stayOpenNote)}
        </p>
      </div>
    </Modal>
  );
}

/**
 * The 80mm paper — a fifth on the `.receipt` roll, in its own
 * `data-print-scope="check"` so it never prints superimposed on the cheque the
 * till keeps mounted (the print rules live with the look stylesheets, in the
 * pre-bill check section of `themes/paper.css`).
 *
 * Printed from the invoice the `printCheck` command RETURNED, so the time on
 * the paper is the time the server recorded and the figures are the ones it
 * priced. Arabic names first with the English one beneath, like the receipt.
 */
export function PreBillCheckPaper({
  invoice,
  splitGuests,
  settings,
  tableName,
}: {
  invoice: Invoice;
  splitGuests: number | null;
  settings: RestaurantSettings | null;
  tableName: string | null;
}) {
  const screenT = useTranslate();
  const { locale: screenLocale } = useI18n();
  const screenFormat = useMoney();
  const logoUrl = useStoreLogo();
  // The store's receipt language (owner, 2026-10-08), not the screen's.
  const locale: "ar" | "en" = settings?.receiptLanguage ?? (screenLocale === "en" ? "en" : "ar");
  const sameLanguage = locale === screenLocale;
  return (
    <PreBillCheckPaperBody
      invoice={invoice}
      splitGuests={splitGuests}
      receiptHeader={settings?.receiptHeader ?? null}
      timezone={settings?.timezone ?? null}
      footer={receiptFooterForLocale(settings, locale)}
      tableName={tableName}
      t={sameLanguage ? screenT : translatorFor(locale)}
      locale={locale}
      direction={locale === "en" ? "ltr" : "rtl"}
      format={sameLanguage ? screenFormat : moneyFormatter(locale)}
      logoUrl={logoUrl}
      // D153 — «الكابتن: ⟨first name⟩», off the bill itself.
      captainName={invoice.captain?.name ?? null}
    />
  );
}

/**
 * The paper itself, with no hooks: the till renders it through
 * `PreBillCheckPaper`, and the print computer renders the SAME markup from a
 * PRE_BILL_CHECK job's frozen snapshot (`coordinatorArtifact`), so the check
 * on the receipt printer is the check the till would have printed.
 */
export function PreBillCheckPaperBody({
  invoice,
  splitGuests,
  receiptHeader,
  timezone,
  tableName,
  t,
  locale,
  direction,
  format,
  captainName = null,
  footer = null,
  logoUrl = null,
}: {
  invoice: Invoice;
  splitGuests: number | null;
  receiptHeader: string | null;
  timezone: string | null | undefined;
  tableName: string | null;
  t: TranslateFn;
  locale: "ar" | "en";
  direction: "rtl" | "ltr";
  format: Format;
  /**
   * D153 — the bill's captain («الكابتن»), printed as the first name; null
   * prints no line. Never the staff member who opened the bill.
   */
  captainName?: string | null;
  /** The store's footer in this slip's language. */
  footer?: string | null;
  /** The store's logo, beside its name in the head. */
  logoUrl?: string | null;
}) {
  const groups = groupPlates(checkPlates(invoice.items), format);
  const money = checkMoney(invoice);
  // The charge rows carry bare figures ("67.2"), the reference's style; only
  // the total names the currency.
  const rows = checkSumRows(invoice, money, t, paperAmount);

  const items: PaperItem[] = groups.map((group) => ({
    key: group.key,
    // One language per slip, like the reference: the Arabic name on an Arabic
    // slip, the English one (when recorded) on an English slip.
    name: locale === "en" && group.plateNameEn ? group.plateNameEn : group.plateNameAr,
    details: [group.comboLabel, group.substitutionLabel, group.addonLabel]
      .filter((text): text is string => Boolean(text))
      .map((text) => ({ text })),
    quantity: group.quantity,
    unitPrice: group.quantity > 0 ? group.total / group.quantity : group.total,
    total: group.total,
  }));
  const seated = invoice.tableIds.length > 0 && tableName !== null;
  const facts: PaperFact[] = [
    orderFact(t, invoice.dailySequence, seated ? tableName : null),
    {
      key: "check-in",
      label: t((m) => m.paper.checkIn),
      value: invoice.openedAt ? paperStamp(invoice.openedAt, timezone) : "",
      ltr: true,
    },
    // Check out = when THIS paper was printed (owner, 2026-10-08): the time
    // the server recorded the check, or now on a browser print before it.
    {
      key: "check-out",
      label: t((m) => m.paper.checkOut),
      value: paperStamp(invoice.lastCheck?.at ?? new Date().toISOString(), timezone),
      ltr: true,
    },
    ...peopleFacts(t, captainName),
  ];
  // The discount line, and the subtotal it is taken off, only when there is
  // a discount (owner, 2026-10-08: "if there is no discount, not need to show it").
  const discounted = rows.some((row) => row.key === "discount");
  const sums = rows.filter((row) => !row.strong && (discounted || row.key !== "subtotal"));

  return (
    <div
      className="receipt pre-bill-check"
      dir={direction}
      lang={locale}
      aria-hidden="true"
      data-testid="pre-bill-check-paper"
    >
      <PaperHead
        storeName={receiptHeader}
        logoUrl={logoUrl}
        place={null}
        facts={facts}
      />

      <PaperItems items={items} t={t} />

      {sums.map((row) => (
        <PaperSumRow key={row.key} label={row.label} value={row.value} testId={`pre-bill-check-paper-${row.key}`} />
      ))}

      <PaperTotal label={t((m) => m.paper.total)} value={format(money.toPay)} status={null} />

      {splitGuests !== null ? (
        <div
          style={{ marginTop: "1.5mm", textAlign: "center", fontWeight: 700 }}
          data-testid="pre-bill-check-paper-split"
        >
          {splitLine(money.toPay, splitGuests, t, format)}
        </div>
      ) : null}

      <PaperFooter text={footer ?? null} poweredBy={t((m) => m.paper.poweredBy)} />
    </div>
  );
}
